'use client';

import { Edit } from '@byway/icons';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useRef } from 'react';
import { Button } from '~/components/Button';
import { getCountryIdFromDestinationInUrl } from '~/constants/countries';
import { buildMapEntryHref } from '~/constants/maps';
import { getPathname, usePathname } from '~/i18n/navigation';
import { trackEvent } from '~/utils';
import styles from './BuildTripButton.module.css';

type BuildTripButtonProps = {
  variant?: 'heroCta' | 'neutral';
  width?: string;
};

export function BuildTripButton({ variant = 'neutral' }: BuildTripButtonProps) {
  const t = useTranslations();
  const locale = useLocale();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  const countryName = pathname?.split('/')[2];
  const countryId = countryName ? getCountryIdFromDestinationInUrl(countryName) : null;
  const analyticsEvent = variant === 'heroCta' ? 'header_create' : 'megamenu_create';

  const handleCreateTripClick = () => {
    trackEvent(analyticsEvent, {
      event_category: 'maps',
      event_action: 'click',
      event_label: analyticsEvent
    });

    const href = buildMapEntryHref(locale, countryId ? { countryId: String(countryId) } : undefined);
    globalThis.open(getPathname({ href, locale }), '_self');
  };

  if (variant === 'heroCta')
    return (
      <Button
        data-ua-click="header_create"
        dataTestId="build-trip-button-header"
        ref={triggerRef}
        variant="neutral"
        onPress={handleCreateTripClick}
      >
        <div className={clsx(styles.buttonContent, styles.heroCtaButtonContent)}>
          <Edit size="large" decorative />
          <span className={styles.buttonText}>{t('navigation.trip_create')}</span>
        </div>
      </Button>
    );

  return (
    <>
      {/* MOBILE */}
      <div className={styles.mobile}>
        <Button
          data-ua-click="megamenu_create"
          ref={triggerRef}
          dataTestId="build-trip-button-megamenu"
          variant="icon"
          ariaLabel={t('aria.trip_create')}
          size="small"
          onPress={handleCreateTripClick}
        >
          <Edit size="large" decorative />
        </Button>
      </div>
      {/* DESKTOP */}
      <div className={styles.desktop}>
        <Button
          data-ua-click="megamenu_create"
          dataTestId="build-trip-button"
          ref={triggerRef}
          variant="neutral"
          size="small"
          onPress={handleCreateTripClick}
        >
          <div className={styles.buttonContent}>
            <Edit size="large" decorative />
            <span className={clsx(styles.desktop, styles.buttonText)}>{t('navigation.trip_create')}</span>
          </div>
        </Button>
      </div>
    </>
  );
}
